import { createContext, useContext, useState, useCallback, useEffect } from "react";

const SelectionContext = createContext(null);

export const SelectionProvider = ({ children }) => {
  const [selectedBlocks, setSelectedBlocks] = useState([]);

  // Clear selection if user returned from Stripe checkout without completing
  useEffect(() => {
    const pending = sessionStorage.getItem("stripe_checkout_pending");
    if (pending === "true") {
      sessionStorage.removeItem("stripe_checkout_pending");
      // User returned without success (success page clears this itself)
      if (!window.location.pathname.includes("/success")) {
        setSelectedBlocks([]);
        // Release the pending blocks on the server
        const sessionId = sessionStorage.getItem("stripe_session_id");
        if (sessionId) {
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
          fetch(`${BACKEND_URL}/api/release-blocks`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId }),
          }).catch(() => {});
          sessionStorage.removeItem("stripe_session_id");
        }
      }
    }
  }, []);

  const getBuyUrl = useCallback(() => {
    if (selectedBlocks.length === 0) return "/buy";
    if (selectedBlocks.length === 1) {
      return `/buy?row=${selectedBlocks[0].row}&col=${selectedBlocks[0].col}`;
    }
    return `/buy?blocks=${encodeURIComponent(JSON.stringify(selectedBlocks))}`;
  }, [selectedBlocks]);

  return (
    <SelectionContext.Provider value={{ selectedBlocks, setSelectedBlocks, getBuyUrl }}>
      {children}
    </SelectionContext.Provider>
  );
};

export const useSelection = () => {
  const ctx = useContext(SelectionContext);
  if (!ctx) throw new Error("useSelection must be used within SelectionProvider");
  return ctx;
};

/**
 * Hook: returns a unified click handler for any "Buy Pixels" button.
 * - If blocks are selected → navigate to /buy with params
 * - If no blocks selected → show NoBlocksModal (matches hero behavior)
 */
export const useBuyAction = () => {
  const { selectedBlocks, getBuyUrl } = useSelection();
  const [showNoBlocksModal, setShowNoBlocksModal] = useState(false);

  const handleBuyClick = useCallback((e) => {
    if (e) e.preventDefault();
    if (selectedBlocks.length > 0) {
      return { shouldNavigate: true, url: getBuyUrl() };
    }
    setShowNoBlocksModal(true);
    return { shouldNavigate: false };
  }, [selectedBlocks, getBuyUrl]);

  const dismissModal = useCallback(() => setShowNoBlocksModal(false), []);

  return { handleBuyClick, showNoBlocksModal, dismissModal, selectedBlocks, getBuyUrl };
};
